import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import firebase from 'firebase/app';
import { useCustomerContext } from './customerContext';
import { useAuthContext } from './authContext';
import { useClientConfig } from './clientConfigContext';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { getPhotoSession, recoverFieldPhotos, updatePhotoSessionContext, refreshPhotoTags } from '../utils/fieldPhotos';
import { useOfflineModeContext } from './offlineContext';

const Context = createContext({ session: null });
export const useFieldPhotoContext = () => useContext(Context);

export const FieldPhotoProvider = ({ children }) => {
  const { customer, startTime } = useCustomerContext();
  const { authUser } = useAuthContext();
  const { features } = useClientConfig();
  const { networkState } = useOfflineModeContext();
  const [session, setSession] = useState(null);
  const previous = useRef(null);
  const uid = firebase.auth().currentUser?.uid;
  const authorId = authUser?.id;
  const customerId = customer?.id;
  const enabled = features.fieldPhotos;
  const online = networkState.online;
  const { reloadOperations } = useOfflineSync(enabled ? 'field_photos' : null, {}, '/customer-action');

  useEffect(() => {
    if (enabled && uid && authorId && online) refreshPhotoTags().catch(() => {});
  }, [enabled, uid, authorId, online]);

  useEffect(() => {
    if (!enabled || !uid || !authorId) return;
    const recover = () => recoverFieldPhotos(uid).then(() => reloadOperations && reloadOperations()).catch(() => {});
    recover();
    window.addEventListener('online', recover);
    window.addEventListener('focus', recover);
    return () => { window.removeEventListener('online', recover); window.removeEventListener('focus', recover); };
  }, [uid, authorId, enabled, reloadOperations]);

  useEffect(() => {
    let cancelled = false;
    const change = async () => {
      if (!enabled || !uid || !authorId) { setSession(null); return; }
      const next = customerId ? await getPhotoSession(uid, { id: customerId }, startTime) : null;
      const last = previous.current;
      if (last && last.id !== next?.id) await updatePhotoSessionContext(last, { visitEndedAt: new Date().toISOString() });
      if (!cancelled) { previous.current = next; setSession(next); }
    };
    change().catch(() => {});
    return () => { cancelled = true; };
  }, [customerId, startTime, uid, authorId, enabled]);

  const value = useMemo(() => ({ session }), [session]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
};
